"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import type { KeyboardEvent } from "react";
import { useEffect, useMemo, useRef, useState, useId } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import { commonApiFetch } from "@/services/api/common-api";
import CommonProfileSearchItems from "../profile-search/CommonProfileSearchItems";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getSelectableIdentity, getSelectableIdentityOption, type SelectableIdentityOption } from "@/components/utils/input/profile-search/getSelectableIdentity";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
export enum IdentitySearchSize {
  SM = "SM",
  MD = "MD",
}
const MIN_SEARCH_LENGTH = 3;
type IdentitySearchDraft = {
  readonly value: string;
  readonly baseResolvedDisplayValue: string | null;
  readonly preservedResolvedValues: readonly (string | null)[];
};
export default function IdentitySearch(t0) {
  const $ = _c(99);
  const {
    identity,
    size: t1,
    label: t2,
    error: t3,
    errorMessage,
    autoFocus: t4,
    selectedDisplayValue,
    clearable: t5,
    disabled: t6,
    dropdownListClassName,
    onSelectionChange,
    setIdentity
  } = t0;
  const size = t1 === undefined ? IdentitySearchSize.MD : t1;
  const label = t2 === undefined ? "Identity" : t2;
  const error = t3 === undefined ? false : t3;
  const autoFocus = t4 === undefined ? false : t4;
  const clearable = t5 === undefined ? true : t5;
  const disabled = t6 === undefined ? false : t6;
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      [IdentitySearchSize.SM]: "tw-py-3",
      [IdentitySearchSize.MD]: "tw-pb-3 tw-pt-3"
    };
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const INPUT_CLASSES = t7;
  let t8;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      [IdentitySearchSize.SM]: "tw-text-sm",
      [IdentitySearchSize.MD]: "tw-text-md"
    };
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const LABEL_CLASSES = t8;
  let t9;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      [IdentitySearchSize.SM]: "tw-h-4 tw-w-4",
      [IdentitySearchSize.MD]: "tw-h-5 tw-w-5"
    };
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const SEARCH_ICON_SIZE_CLASSES = t9;
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const resolvedDisplayValue = selectedDisplayValue ?? identity ?? null;
  const [searchCriteriaDraft, setSearchCriteriaDraft] = useState(null);
  const shouldUseDraft = !disabled && searchCriteriaDraft !== null && (searchCriteriaDraft.baseResolvedDisplayValue === resolvedDisplayValue || searchCriteriaDraft.preservedResolvedValues.some(value => value === resolvedDisplayValue));
  const searchCriteria = shouldUseDraft ? searchCriteriaDraft.value : resolvedDisplayValue;
  const [debouncedValue, setDebouncedValue] = useState(searchCriteria);
  let t10;
  if ($[3] !== disabled || $[4] !== searchCriteria) {
    t10 = () => setDebouncedValue(disabled ? null : searchCriteria);
    $[3] = disabled;
    $[4] = searchCriteria;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  let t11;
  if ($[6] !== disabled || $[7] !== searchCriteria) {
    t11 = [disabled, searchCriteria];
    $[6] = disabled;
    $[7] = searchCriteria;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  useDebounce(t10, 200, t11);
  let t12;
  let t13;
  if ($[9] !== debouncedValue) {
    t12 = [QueryKey.PROFILE_SEARCH, {
      param: debouncedValue,
      only_profile_owners: "true"
    }];
    t13 = async () => await commonApiFetch({
      endpoint: "community-members",
      params: {
        param: debouncedValue ?? "",
        only_profile_owners: "true"
      }
    });
    $[9] = debouncedValue;
    $[10] = t12;
    $[11] = t13;
  } else {
    t12 = $[10];
    t13 = $[11];
  }
  const t14 = !disabled && !!debouncedValue && debouncedValue.length >= MIN_SEARCH_LENGTH;
  let t15;
  if ($[12] !== t12 || $[13] !== t13 || $[14] !== t14) {
    t15 = {
      queryKey: t12,
      queryFn: t13,
      enabled: t14
    };
    $[12] = t12;
    $[13] = t13;
    $[14] = t14;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const {
    data
  } = useQuery(t15);
  let t16;
  if ($[16] !== data || $[17] !== disabled) {
    t16 = disabled ? [] : data ?? [];
    $[16] = data;
    $[17] = disabled;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  const searchResults = t16;
  const [isOpen, setIsOpen] = useState(false);
  const [manualHighlightedIndex, setManualHighlightedIndex] = useState(null);
  const [highlightedOptionId, setHighlightedOptionId] = useState(undefined);
  if (disabled) {
    if (isOpen) {
      setIsOpen(false);
    }
    if (manualHighlightedIndex !== null) {
      setManualHighlightedIndex(null);
    }
  }
  const isDropdownOpen = !disabled && isOpen;
  let t17;
  bb0: {
    if (searchResults.length === 0 || !identity) {
      t17 = null;
      break bb0;
    }
    let t18;
    if ($[19] !== identity) {
      t18 = profile => {
        const value_0 = getSelectableIdentity(profile);
        return value_0?.toLowerCase() === identity.toLowerCase();
      };
      $[19] = identity;
      $[20] = t18;
    } else {
      t18 = $[20];
    }
    const matchingIndex = searchResults.findIndex(t18);
    t17 = matchingIndex >= 0 ? matchingIndex : null;
  }
  const selectedResultIndex = t17;
  let t18;
  bb1: {
    if (!isDropdownOpen || searchResults.length === 0) {
      t18 = null;
      break bb1;
    }
    if (manualHighlightedIndex !== null) {
      t18 = Math.min(manualHighlightedIndex, searchResults.length - 1);
      break bb1;
    }
    t18 = selectedResultIndex;
  }
  const effectiveHighlightedIndex = t18;
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      setIsOpen(false);
      setManualHighlightedIndex(null);
    };
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  const closeDropdown = t19;
  let t20;
  if ($[22] !== disabled || $[23] !== onSelectionChange || $[24] !== resolvedDisplayValue || $[25] !== setIdentity) {
    t20 = (newValue, options) => {
      if (disabled) {
        return;
      }
      const draftValue = options?.displayValue ?? newValue ?? "";
      setIdentity(newValue);
      onSelectionChange?.(options?.selection ?? null);
      setSearchCriteriaDraft({
        value: draftValue,
        baseResolvedDisplayValue: resolvedDisplayValue,
        preservedResolvedValues: [newValue, options?.displayValue ?? null]
      });
      closeDropdown();
    };
    $[22] = disabled;
    $[23] = onSelectionChange;
    $[24] = resolvedDisplayValue;
    $[25] = setIdentity;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  const onValueChange = t20;
  let t21;
  if ($[27] !== disabled || $[28] !== searchCriteria?.length) {
    t21 = newV => {
      if (disabled) {
        closeDropdown();
        return;
      }
      if (newV) {
        const len = searchCriteria?.length ?? 0;
        setIsOpen(len >= MIN_SEARCH_LENGTH);
        return;
      }
      closeDropdown();
    };
    $[27] = disabled;
    $[28] = searchCriteria?.length;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  const onFocusChange = t21;
  let t22;
  if ($[30] !== clearable || $[31] !== disabled || $[32] !== onSelectionChange || $[33] !== resolvedDisplayValue || $[34] !== setIdentity) {
    t22 = newV_0 => {
      if (disabled) {
        return;
      }
      setSearchCriteriaDraft({
        value: newV_0 ?? "",
        baseResolvedDisplayValue: resolvedDisplayValue,
        preservedResolvedValues: []
      });
      const len_0 = newV_0?.length ?? 0;
      setIsOpen(len_0 >= MIN_SEARCH_LENGTH);
      if (!newV_0 && clearable) {
        setIdentity(null);
        onSelectionChange?.(null);
      }
      setManualHighlightedIndex(null);
    };
    $[30] = clearable;
    $[31] = disabled;
    $[32] = onSelectionChange;
    $[33] = resolvedDisplayValue;
    $[34] = setIdentity;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  const onSearchCriteriaChange = t22;
  let t23;
  if ($[36] !== disabled || $[37] !== onValueChange) {
    t23 = profile_0 => {
      if (disabled) {
        return false;
      }
      const nextSelection = getSelectableIdentityOption(profile_0);
      if (!nextSelection) {
        return false;
      }
      onValueChange(nextSelection.value, {
        displayValue: nextSelection.label,
        selection: nextSelection
      });
      return true;
    };
    $[36] = disabled;
    $[37] = onValueChange;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const selectProfile = t23;
  const wrapperRef = useRef(null);
  useClickAway(wrapperRef, closeDropdown);
  let t24;
  if ($[39] !== disabled) {
    t24 = () => {
      if (!disabled) {
        closeDropdown();
      }
    };
    $[39] = disabled;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  useKeyPressEvent("Escape", t24);
  const inputRef = useRef(null);
  const shouldAutoFocus = useRef(autoFocus);
  let t25;
  let t26;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => {
      if (shouldAutoFocus.current) {
        inputRef.current?.focus();
      }
    };
    t26 = [];
    $[41] = t25;
    $[42] = t26;
  } else {
    t25 = $[41];
    t26 = $[42];
  }
  useEffect(t25, t26);
  let t27;
  if ($[43] !== disabled || $[44] !== effectiveHighlightedIndex || $[45] !== searchResults || $[46] !== selectProfile) {
    t27 = event => {
      if (disabled) {
        return;
      }
      const resultCount = searchResults.length;
      if (resultCount === 0) {
        return;
      }
      const maxIndex = resultCount - 1;
      const currentHighlightIndex = effectiveHighlightedIndex;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setIsOpen(true);
        setManualHighlightedIndex(currentHighlightIndex === null || currentHighlightIndex >= maxIndex ? 0 : currentHighlightIndex + 1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setIsOpen(true);
        setManualHighlightedIndex(currentHighlightIndex === null || currentHighlightIndex <= 0 ? maxIndex : currentHighlightIndex - 1);
        return;
      }
      if (event.key === "Enter" && effectiveHighlightedIndex !== null) {
        event.preventDefault();
        const profile_1 = searchResults[effectiveHighlightedIndex];
        if (profile_1 !== undefined && selectProfile(profile_1)) {
          inputRef.current?.form?.requestSubmit();
        }
      }
    };
    $[43] = disabled;
    $[44] = effectiveHighlightedIndex;
    $[45] = searchResults;
    $[46] = selectProfile;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  const handleArrowNavigation = t27;
  const hasIdentity = identity !== null && identity.length > 0;
  const t28 = searchCriteria ?? "";
  let t29;
  if ($[48] !== onSearchCriteriaChange) {
    t29 = e => onSearchCriteriaChange(e.target.value);
    $[48] = onSearchCriteriaChange;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  let t31;
  if ($[50] !== onFocusChange) {
    t30 = () => onFocusChange(true);
    t31 = e_0 => {
      const next = e_0.relatedTarget as Node | null;
      if (!next || !wrapperRef.current?.contains(next)) {
        onFocusChange(false);
      }
    };
    $[50] = onFocusChange;
    $[51] = t30;
    $[52] = t31;
  } else {
    t30 = $[51];
    t31 = $[52];
  }
  let t32;
  if ($[53] !== handleArrowNavigation) {
    t32 = event_0 => handleArrowNavigation(event_0);
    $[53] = handleArrowNavigation;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  const t33 = isDropdownOpen && highlightedOptionId ? highlightedOptionId : undefined;
  const t34 = `${INPUT_CLASSES[size]} ${error ? "tw-caret-error tw-ring-error focus:tw-border-error focus:tw-ring-error" : "tw-caret-primary-400 tw-ring-iron-700 hover:tw-ring-iron-650 focus:tw-border-blue-500 focus:tw-ring-primary-400"} tw-peer tw-form-input tw-block tw-w-full tw-appearance-none tw-rounded-lg tw-border-0 tw-border-iron-700 tw-bg-iron-900 tw-pl-9 tw-pr-4 tw-text-base tw-font-medium tw-shadow-sm tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset sm:tw-text-sm ${searchCriteria ? "tw-text-primary-400 focus:tw-text-white" : "tw-text-white"} disabled:tw-cursor-not-allowed disabled:tw-opacity-70`;
  let t35;
  if ($[55] !== disabled || $[56] !== inputId || $[57] !== isDropdownOpen || $[58] !== listboxId || $[59] !== t28 || $[60] !== t29 || $[61] !== t30 || $[62] !== t31 || $[63] !== t32 || $[64] !== t33 || $[65] !== t34) {
    t35 = <input ref={inputRef} type="text" value={t28} onChange={t29} onFocus={t30} onBlur={t31} onKeyDown={t32} id={inputId} disabled={disabled} autoComplete="off" role="combobox" aria-autocomplete="list" aria-disabled={disabled} aria-expanded={isDropdownOpen} aria-controls={listboxId} aria-activedescendant={t33} className={t34} placeholder=" " />;
    $[55] = disabled;
    $[56] = inputId;
    $[57] = isDropdownOpen;
    $[58] = listboxId;
    $[59] = t28;
    $[60] = t29;
    $[61] = t30;
    $[62] = t31;
    $[63] = t32;
    $[64] = t33;
    $[65] = t34;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  const t36 = `${"tw-top-3.5"} ${SEARCH_ICON_SIZE_CLASSES[size]} tw-pointer-events-none tw-absolute tw-left-3 tw-text-iron-300`;
  let t37;
  if ($[67] !== t36) {
    t37 = <MagnifyingGlassIcon className={t36} aria-hidden="true" />;
    $[67] = t36;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== clearable || $[70] !== disabled || $[71] !== hasIdentity || $[72] !== onValueChange) {
    t38 = !disabled && clearable && hasIdentity && <button type="button" aria-label="Clear identity" onClick={() => onValueChange(null)} className="tw-top-3.5 tw-absolute tw-right-3 tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-400 tw-transition tw-duration-300 tw-ease-out hover:tw-text-error focus:tw-outline-none focus:tw-ring-0"><FontAwesomeIcon icon={faXmark} className="tw-h-4 tw-w-4 tw-flex-shrink-0" /></button>;
    $[69] = clearable;
    $[70] = disabled;
    $[71] = hasIdentity;
    $[72] = onValueChange;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  const t39 = `${LABEL_CLASSES[size]} ${error ? "peer-placeholder-shown:tw-top-1/4 peer-placeholder-shown:-tw-translate-y-1/4 peer-focus:tw-text-error" : "peer-placeholder-shown:tw-top-1/2 peer-placeholder-shown:-tw-translate-y-1/2 peer-focus:tw-text-primary-400"} tw-absolute tw-start-1 tw-top-2 tw-z-10 tw-ml-6 tw-origin-[0] -tw-translate-y-4 tw-scale-75 tw-transform tw-cursor-text tw-rounded-lg tw-bg-iron-900 tw-px-2 tw-font-medium tw-text-iron-500 tw-duration-300 peer-placeholder-shown:tw-top-1/2 peer-placeholder-shown:-tw-translate-y-1/2 peer-placeholder-shown:tw-scale-100 peer-focus:tw-top-2 peer-focus:-tw-translate-y-4 peer-focus:tw-scale-75 peer-focus:tw-bg-iron-900 peer-focus:tw-px-2 peer-focus:tw-text-primary-400 rtl:peer-focus:tw-left-auto rtl:peer-focus:tw-translate-x-1/4`;
  let t40;
  if ($[74] !== inputId || $[75] !== label || $[76] !== t39) {
    t40 = <label htmlFor={inputId} className={t39}>{label}</label>;
    $[74] = inputId;
    $[75] = label;
    $[76] = t39;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== selectProfile) {
    t41 = profile_2 => {
      if (!profile_2) {
        return;
      }
      selectProfile(profile_2);
    };
    $[78] = selectProfile;
    $[79] = t41;
  } else {
    t41 = $[79];
  }
  let t42;
  if ($[80] !== dropdownListClassName || $[81] !== effectiveHighlightedIndex || $[82] !== identity || $[83] !== isDropdownOpen || $[84] !== listboxId || $[85] !== searchCriteria || $[86] !== searchResults || $[87] !== t41) {
    t42 = <CommonProfileSearchItems open={isDropdownOpen} selected={identity} searchCriteria={searchCriteria} profiles={searchResults} highlightedIndex={effectiveHighlightedIndex} listboxId={listboxId} listClassName={dropdownListClassName} onHighlightedOptionIdChange={setHighlightedOptionId} onProfileSelect={t41} />;
    $[80] = dropdownListClassName;
    $[81] = effectiveHighlightedIndex;
    $[82] = identity;
    $[83] = isDropdownOpen;
    $[84] = listboxId;
    $[85] = searchCriteria;
    $[86] = searchResults;
    $[87] = t41;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== error || $[90] !== errorMessage) {
    t43 = error && <div className="tw-relative tw-flex tw-items-center tw-gap-x-2 tw-pt-1.5"><FontAwesomeIcon icon={faCircleExclamation} className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-error" aria-hidden="true" /><div className="tw-text-xs tw-font-medium tw-text-error">{errorMessage ?? "Please enter identity"}</div></div>;
    $[89] = error;
    $[90] = errorMessage;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  let t44;
  if ($[92] !== t35 || $[93] !== t37 || $[94] !== t38 || $[95] !== t40 || $[96] !== t42 || $[97] !== t43) {
    t44 = <div className="tw-group tw-relative tw-w-full" ref={wrapperRef}>{t35}{t37}{t38}{t40}{t42}{t43}</div>;
    $[92] = t35;
    $[93] = t37;
    $[94] = t38;
    $[95] = t40;
    $[96] = t42;
    $[97] = t43;
    $[98] = t44;
  } else {
    t44 = $[98];
  }
  return t44;
}